import { CTATile } from "@faire/retailer-visitor-shared/components/Carousel/CTATile";
import { handleGenerateProductLinkProps } from "@faire/retailer-visitor-shared/components/SearchProducts/utils";
import { TrackedLinkContext } from "@faire/retailer-visitor-shared/components/TrackedLink/index";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { productUrlParametersILeanProductTileAdapter } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductLink/useProductLinkProps";
import { ProductTile } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ProductTile";
import { useUserStore } from "@faire/retailer-visitor-shared/stores/domain/useUserStore";
import { BrandImage } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/SearchPageHeaderAndResults/__internal__/SearchPageHeader/__internal__/SearchPageBrandSearch/BrandSearch/__internal__/BrandImage";
import { BrandMinimum } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/SearchPageHeaderAndResults/__internal__/SearchPageHeader/__internal__/SearchPageBrandSearch/BrandSearch/__internal__/BrandMinimum";
import { BrandName } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/SearchPageHeaderAndResults/__internal__/SearchPageHeader/__internal__/SearchPageBrandSearch/BrandSearch/__internal__/BrandName";
import { BrandRating } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/SearchPageHeaderAndResults/__internal__/SearchPageHeader/__internal__/SearchPageBrandSearch/BrandSearch/__internal__/BrandRating";
import { BrandSearchProductTileTrackingContainer } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/SearchPageHeaderAndResults/__internal__/SearchPageHeader/__internal__/SearchPageBrandSearch/BrandSearch/__internal__/BrandSearchProductTileTrackingContainer";
import { MaybeInsiderMessageOrBrandBasedIn } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/SearchPageHeaderAndResults/__internal__/SearchPageHeader/__internal__/SearchPageBrandSearch/BrandSearch/__internal__/MaybeInsiderMessageOrBrandBasedIn";
import { ToBrandButton } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/SearchPageHeaderAndResults/__internal__/SearchPageHeader/__internal__/SearchPageBrandSearch/BrandSearch/__internal__/ToBrandButton";
import { BRAND_SEARCH_CAROUSEL_CONTAINER_NAME } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/SearchPageHeaderAndResults/__internal__/SearchPageHeader/__internal__/SearchPageBrandSearch/BrandSearch/BrandSearch";
import { BrandSearchContext } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/SearchPageHeaderAndResults/__internal__/SearchPageHeader/__internal__/SearchPageBrandSearch/BrandSearch/BrandSearchContext";
import { useSearchPageProductSearchString } from "@faire/retailer-visitor-shared/views/search-v2/SearchPageQueryParams/useSearchPageProductSearchString";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Column, Grid, Row } from "@faire/web--source/slate/Layout";
import { Spacer, getSpacing } from "@faire/web--source/slate/spacing";
import { useEffectEvent } from "@faire/web--source/ui/hooks/useEffectEvent";
import { ResponsiveContentV2 } from "@faire/web--source/ui/ResponsiveContentV2";
import { trackSearchProductCarouselView } from "@faire/web-api--source/events/search/view/productCarousel";
import { IBrandSearchQueryUXMessaging } from "@faire/web-api--source/indigofair/data/IBrandSearchQueryUXMessaging";
import { IClickInfo } from "@faire/web-api--source/indigofair/data/IClickInfo";
import { ILeanProductTile } from "@faire/web-api--source/indigofair/data/ILeanProductTile";
import React, { useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

interface IImprovedBrandSearch {
  isLoading: boolean;
  itemsPerRow: number;
  ctaText?: string;
}

export const ImprovedBrandSearch: React.FC<
  IImprovedBrandSearch & {
    products: ILeanProductTile[] | undefined;
  }
> = ({ isLoading, itemsPerRow, products, ctaText }) => {
  const { requestId, brand, insiderMessage } = useContext(BrandSearchContext);
  const onView = useEffectEvent(() => {
    const productTokensSeparatedBySpace = products
      ?.map((item) => item.product?.token)
      .join(" ");

    if (productTokensSeparatedBySpace) {
      trackSearchProductCarouselView(
        BRAND_SEARCH_CAROUSEL_CONTAINER_NAME,
        requestId ?? "",
        productTokensSeparatedBySpace
      );
    }
  });

  useEffect(() => onView(), [onView]);

  if (!brand && !isLoading) {
    return null;
  }

  return (
    <TrackedLinkContext.Provider
      value={{
        type: IClickInfo.Type.BRAND,
        containerName: BRAND_SEARCH_CAROUSEL_CONTAINER_NAME,
      }}
    >
      <ResponsiveContentV2
        mobile={
          <ImprovedBrandSearchMobile
            isLoading={isLoading}
            products={products}
            ctaText={ctaText}
            brand={brand}
            insiderMessage={insiderMessage}
          />
        }
        tabletAndAbove={
          <ImprovedBrandSearchDesktop
            isLoading={isLoading}
            itemsPerRow={itemsPerRow}
            products={products}
            ctaText={ctaText}
            brand={brand}
            insiderMessage={insiderMessage}
          />
        }
      />
    </TrackedLinkContext.Provider>
  );
};

/**
 * Desktop header, used for viewport above mobile, which includes tablet
 */
const ImprovedBrandHeaderDesktop: React.FC<{
  isLoading: boolean;
  ctaText?: string;
  brand: ILeanProductTile.IBrand | undefined;
  insiderMessage: IBrandSearchQueryUXMessaging | undefined;
}> = ({ isLoading, ctaText, brand, insiderMessage }) => {
  return (
    <Row justify="space-between">
      <Row>
        <BrandImage isLoading={isLoading} />
        <Spacer size="2x" />
        <Column justify="center">
          <BrandName isLoading={isLoading} />
          <Spacer size="1/2x" />

          <Row align="center" gap="1x">
            <BrandRating brand={brand} />
            <BrandMinimum isLoading={isLoading} brand={brand} />
            <MaybeInsiderMessageOrBrandBasedIn
              isLoading={isLoading}
              brand={brand}
              insiderMessage={insiderMessage}
            />
          </Row>
        </Column>
      </Row>
      <ToBrandButton isLoading={isLoading} ctaText={ctaText} />
    </Row>
  );
};

const ImprovedBrandSearchMobile: React.FC<{
  isLoading: boolean;
  products: ILeanProductTile[] | undefined;
  ctaText?: string;
  brand: ILeanProductTile.IBrand | undefined;
  insiderMessage: IBrandSearchQueryUXMessaging | undefined;
}> = ({ isLoading, products, ctaText, brand, insiderMessage }) => {
  const { products: mobileProducts, showCta } = getProductsWithCta(
    products,
    2,
    true
  );
  const productSearchString = useSearchPageProductSearchString();
  const location = useLocation();
  const { user } = useUserStore(["user"]);
  return (
    <Column>
      <Row>
        <BrandImage isLoading={isLoading} />
        <Spacer size="1x" />
        <StyledMobileBrandHeaderTextContainer justify="space-between">
          <BrandName isLoading={isLoading} />
          <Row gap="1x">
            <BrandRating brand={brand} />
            <BrandMinimum isLoading={isLoading} brand={brand} />
            <MaybeInsiderMessageOrBrandBasedIn
              isLoading={isLoading}
              brand={brand}
              insiderMessage={insiderMessage}
            />
          </Row>
        </StyledMobileBrandHeaderTextContainer>
      </Row>
      <Spacer size="2x" />
      <BrandSearchProductTileTrackingContainer>
        <Grid columns={2} rowGap={16}>
          {mobileProducts?.map((product) => (
            <ProductTile
              key={product.product?.token}
              showEncapsulatedQuickAdd
              productTile={product}
              recordProductTileImpressionForAds
              generateProductLinkProps={handleGenerateProductLinkProps(
                productSearchString,
                productUrlParametersILeanProductTileAdapter(product),
                [],
                location,
                user
              )}
            />
          ))}
        </Grid>
      </BrandSearchProductTileTrackingContainer>
      {showCta ? (
        <>
          <Spacer size="2x" />
          <ToBrandButton isLoading={isLoading} ctaText={ctaText} />
        </>
      ) : null}
    </Column>
  );
};

/**
 * A helper to calculate
 * - when to show cta button
 * - and the number of products show in the grid to keep a max of 2 rows.
 * @param products
 * @param itemsPerRow
 * @returns
 */
const getProductsWithCta = (
  products: ILeanProductTile[] | undefined,
  itemsPerRow: number,
  isMobile?: boolean
) => {
  const nProducts = products?.length ?? 0;
  const gridCapacity = isMobile ? 6 : itemsPerRow * 2 - 1;
  if (nProducts > gridCapacity) {
    return {
      products: products?.slice(0, gridCapacity),
      showCta: true,
    };
  } else {
    return {
      products,
      showCta: false,
    };
  }
};

/**
 * Desktop layout
 */
const ImprovedBrandSearchDesktop: React.FC<
  IImprovedBrandSearch & {
    products: ILeanProductTile[] | undefined;
    brand: ILeanProductTile.IBrand | undefined;
    insiderMessage: IBrandSearchQueryUXMessaging | undefined;
  }
> = ({ isLoading, itemsPerRow, products, brand, insiderMessage }) => {
  const location = useLocation();
  const { brandPagePath, handleBrandClick } = useContext(BrandSearchContext);
  const productsWithCta = getProductsWithCta(products, itemsPerRow);
  const { strictLocalize } = useStrictLocalization();
  const productSearchString = useSearchPageProductSearchString();
  const { user } = useUserStore(["user"]);
  return (
    <Column>
      <ImprovedBrandHeaderDesktop
        isLoading={isLoading}
        brand={brand}
        insiderMessage={insiderMessage}
      />
      <Spacer size="2x" />
      <BrandSearchProductTileTrackingContainer>
        <Grid columns={itemsPerRow} rowGap={16}>
          {productsWithCta.products?.map((product) => (
            <ProductTile
              key={product.product?.token}
              showEncapsulatedQuickAdd
              productTile={product}
              recordProductTileImpressionForAds
              generateProductLinkProps={handleGenerateProductLinkProps(
                productSearchString,
                productUrlParametersILeanProductTileAdapter(product),
                [],
                location,
                user
              )}
            />
          ))}
          {productsWithCta.showCta ? (
            <CTATile
              data={{
                text: strictLocalize({
                  defaultMessage: "Shop all",
                  description: {
                    text: "texts on a button where retailer can click and shop all from a brand.",
                    img: "https://cdn.faire.com/fastly/5dbce627b13fc2e026150331d97bb73bc007318864f8de73909f9ed961448d28.png",
                  },
                }),
                linkTo: brandPagePath,
                alwaysVisible: true,
                onClick: handleBrandClick("shopAll"),
              }}
            />
          ) : null}
        </Grid>
      </BrandSearchProductTileTrackingContainer>
    </Column>
  );
};

const StyledMobileBrandHeaderTextContainer = styled(Column)`
  padding: ${getSpacing("1/2x", 0)};
`;
